
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // Update form event listener to exclude sort-by changes
    this.querySelector('form').addEventListener('input', (event) => {
      if (!event.target.classList.contains('facet-filters__sort')) {
        this.debouncedOnSubmit(event);
      }
    });

    // Add event listener for price range checkboxes to ensure mutual exclusivity
    this.querySelector('form').addEventListener('change', (event) => {
      if (event.target.classList.contains('price-range-filter')) {
        this.handlePriceRangeChange(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer){
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop){
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
      // Check custom price range checkboxes after URL is updated
      setTimeout(() => {
        FacetFiltersForm.checkCustomPriceRangeCheckboxes(document);
      }, 0);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        
        // Check custom price range checkboxes after rendering from fetch
        setTimeout(() => {
          FacetFiltersForm.checkCustomPriceRangeCheckboxes(document);
        }, 0);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    
    // Check custom price range checkboxes after rendering from cache
    setTimeout(() => {
      FacetFiltersForm.checkCustomPriceRangeCheckboxes(document);
    }, 0);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`);
      
      // Check if this is a mobile accordion wrapper and we're on mobile
      const isMobile = window.innerWidth < 990;
      const isMobileAccordion = targetElement && targetElement.classList.contains('mobile-facet-accordion-wrapper');
      
      // On mobile, skip re-rendering mobile accordions to preserve their state
      if (isMobile && isMobileAccordion) {
        // Only update the checkboxes state, not the entire accordion structure
        FacetFiltersForm.updateMobileCheckboxes(element, targetElement);
      } else {
        // Desktop or non-accordion elements: normal full innerHTML replacement
        targetElement.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
  }

  static updateMobileCheckboxes(sourceElement, targetElement) {
    // Update checkboxes without destroying the accordion structure
    const sourceCheckboxes = sourceElement.querySelectorAll('input[type="checkbox"]');
    
    sourceCheckboxes.forEach((sourceCheckbox) => {
      const targetCheckbox = targetElement.querySelector(`#${sourceCheckbox.id}`);
      if (targetCheckbox) {
        // Update checked state
        targetCheckbox.checked = sourceCheckbox.checked;
        if (sourceCheckbox.checked) {
          targetCheckbox.setAttribute('checked', 'checked');
        } else {
          targetCheckbox.removeAttribute('checked');
        }
        
        // Update disabled state
        targetCheckbox.disabled = sourceCheckbox.disabled;
        if (sourceCheckbox.disabled) {
          targetCheckbox.setAttribute('disabled', 'disabled');
        } else {
          targetCheckbox.removeAttribute('disabled');
        }
        
        // Update the label's disabled class if needed
        const targetLabel = targetElement.querySelector(`label[for="${sourceCheckbox.id}"]`);
        const sourceLabel = sourceElement.querySelector(`label[for="${sourceCheckbox.id}"]`);
        if (targetLabel && sourceLabel) {
          if (sourceLabel.classList.contains('mobile-facets__label--disabled')) {
            targetLabel.classList.add('mobile-facets__label--disabled');
          } else {
            targetLabel.classList.remove('mobile-facets__label--disabled');
          }
        }
      }
    });
  }

  static checkCustomPriceRangeCheckboxes(html) {
    // Get all checkboxes directly from the document (this includes both desktop and mobile)
    const allCheckboxes = document.querySelectorAll('.price-range-filter');
    console.log('All price range checkboxes in DOM:', allCheckboxes.length);
    
    // Let's also check specifically for mobile checkboxes
    const mobileCheckboxes = document.querySelectorAll('.mobile-facets .price-range-filter');
    console.log('Mobile checkboxes specifically:', mobileCheckboxes.length);
    
    // Let's see what IDs we're actually finding
    console.log('Checkbox IDs found:', Array.from(allCheckboxes).map(cb => cb.id));
    
    console.log('Current URL:', window.location.search);
    
    // First, uncheck all price range checkboxes to ensure clean state
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
      checkbox.removeAttribute('checked');
    });
    
    // Also uncheck mobile checkboxes specifically
    mobileCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
      checkbox.removeAttribute('checked');
    });
    
    // Then find and check the appropriate one based on URL parameters
    let foundActivePriceRange = false;
    
    // Process all checkboxes - check both desktop and mobile for the same price range
    allCheckboxes.forEach(checkbox => {
      console.log('Checking checkbox:', checkbox.id, 'value:', checkbox.value, 'filterAppendValue:', checkbox.dataset.filterAppendValue);
      
      // Check for range type (has both gte and lte)
      if (checkbox.dataset.filterAppendValue && 
          window.location.search.includes(`lte=${checkbox.dataset.filterAppendValue}`) && 
          window.location.search.includes(`gte=${checkbox.value}`)) {
        console.log('Matched range filter:', checkbox.id);
        checkbox.checked = true;
        checkbox.setAttribute('checked', 'checked');
        foundActivePriceRange = true;
      }
      // Check for gte-only type (like $150+)
      else if (!checkbox.dataset.filterAppendValue && 
               window.location.search.includes(`gte=${checkbox.value}`) &&
               !window.location.search.includes('lte=')) {
        console.log('Matched gte-only filter:', checkbox.id);
        checkbox.checked = true;
        checkbox.setAttribute('checked', 'checked');
        foundActivePriceRange = true;
      }
    });
    
    console.log('Found active price range:', foundActivePriceRange);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.facets__wrapper'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();

    document.querySelectorAll('sort-by').forEach(element => {
      element.init();
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));

    // Only process the first checked price range checkbox to ensure mutual exclusivity
    const customPriceCheckboxes = event.target.closest('form').querySelectorAll('.price-range-filter:checked');
    if (customPriceCheckboxes.length > 0) {
      const checkbox = customPriceCheckboxes[0]; // Only take the first one
      if (checkbox.dataset.urlAppend) {
        formData.append(checkbox.dataset.urlAppend, checkbox.dataset.filterAppendValue);
      }
    }

    // Add sort-by value to formData only if sort_by is not already present
    if (!formData.has('sort_by')) {
      const sortBySelect = document.querySelector('sort-by select');
      if (sortBySelect && sortBySelect.value) {
        formData.append('sort_by', sortBySelect.value);
      }
    }

    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  handlePriceRangeChange(event) {
    if (event.target.checked) {
      // Uncheck all other price range checkboxes
      const allPriceRangeCheckboxes = this.querySelectorAll('.price-range-filter');
      allPriceRangeCheckboxes.forEach(checkbox => {
        if (checkbox !== event.target) {
          checkbox.checked = false;
          checkbox.removeAttribute('checked');
        }
      });
    } else {
      // If unchecking, make sure we clear any conflicting state
      const allPriceRangeCheckboxes = this.querySelectorAll('.price-range-filter');
      allPriceRangeCheckboxes.forEach(checkbox => {
        checkbox.checked = false;
        checkbox.removeAttribute('checked');
      });
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);


// Add new SortBy class to handle sorting separately
class SortBy extends HTMLElement {
  constructor() {
    super();
    this.init();
    this.applyDefaultSort();
  }

  init() {
    this.debouncedOnChange = debounce((event) => {
      this.onSortChange(event);
    }, 500);

    const select = this.querySelector('select');
    if (select) {
      // Remove existing listeners first
      select.removeEventListener('change', this.debouncedOnChange.bind(this));
      // Add new listener
      select.addEventListener('change', this.debouncedOnChange.bind(this));
    }
  }

  applyDefaultSort() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSort = urlParams.get('sort_by');
    const select = this.querySelector('select');
    
    if ((!currentSort || currentSort === '') && select) {
      const bestSellingOption = Array.from(select.options).find(
        option => option.value === 'best-selling'
      );
      
      if (bestSellingOption) {
        select.value = 'best-selling';
        
        urlParams.set('sort_by', 'best-selling');
        FacetFiltersForm.renderPage(urlParams.toString(), null, true);
      }
    }
  }

  onSortChange(event) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', event.target.value);
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }
}

customElements.define('sort-by', SortBy);

// Custom Sort Popup Handler
class CustomSortHandler {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.button = wrapper.querySelector('.custom-sort-button');
    this.popup = wrapper.querySelector('.custom-sort-popup');
    this.closeButton = this.popup?.querySelector('.custom-sort-popup__close');
    this.radioButtons = this.popup?.querySelectorAll('.custom-sort-radio');
    this.escapeHandler = null;
    this.closeTimeout = null;
    
    if (this.button && this.popup) {
      this.init();
    }
  }

  init() {
    const supportsHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    // Open popup on hover over button
    if (supportsHover) {
      this.button.addEventListener('mouseenter', () => {
        this.cancelClose();
        this.openPopup();
      });
    }

    // Don't close immediately when leaving button
    if (supportsHover) {
      this.button.addEventListener('mouseleave', () => {
        this.scheduleClose();
      });
    }

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this.cancelClose();
      if (this.popup.hidden) {
        this.openPopup();
      } else {
        this.closePopup();
      }
    })

    // Keep popup open when hovering over it
    if (supportsHover) {
      this.popup.addEventListener('mouseenter', () => {
        this.cancelClose();
      });
    }

    // Close popup when mouse leaves popup
    if (supportsHover) {
      this.popup.addEventListener('mouseleave', () => {
        this.scheduleClose();
      });
    }

    // Close popup on close button click
    this.closeButton?.addEventListener('click', () => {
      this.closePopup();
    });

    // Handle radio button changes
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleSortChange(e.target);
        }
      });
    });

    // Store handlers so we can remove them later
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && !this.popup.hidden) {
        this.closePopup();
      }
    };
    
    document.addEventListener('keydown', this.escapeHandler);
  }

  scheduleClose() {
    this.closeTimeout = setTimeout(() => {
      this.closePopup();
    }, 150);
  }

  cancelClose() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  openPopup() {
    this.popup.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
  }

  closePopup() {
    this.cancelClose();
    this.popup.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
  }

  handleSortChange(radio) {
    const sortValue = radio.value;
    const sortText = radio.closest('.custom-sort-option').querySelector('.custom-sort-option__text').textContent;
    
    // Update button text
    this.button.querySelector('.custom-sort-button__text').textContent = sortText;
    
    // Close popup
    this.closePopup();
    
    // Trigger sort change
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', sortValue);
    FacetFiltersForm.renderPage(searchParams.toString(), { target: radio });
  }
}

// Initialize all custom sort handlers
function initializeCustomSortHandlers() {
  const wrappers = document.querySelectorAll('.custom-sort-wrapper');
  wrappers.forEach(wrapper => {
    // Avoid re-initializing the same wrapper multiple times
    if (wrapper.dataset.customSortInitialized === 'true') return;
    wrapper.dataset.customSortInitialized = 'true';
    new CustomSortHandler(wrapper);
  });
  ensureGlobalCustomSortEscapeHandler();
}

// Add a single global Escape handler to close any open custom sort popups
let __customSortGlobalEscapeBound = false;
function ensureGlobalCustomSortEscapeHandler() {
  if (__customSortGlobalEscapeBound) return;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openPopups = document.querySelectorAll('.custom-sort-popup:not([hidden])');
    openPopups.forEach((popup) => {
      popup.hidden = true;
      const wrapper = popup.closest('.custom-sort-wrapper');
      const button = wrapper?.querySelector('.custom-sort-button');
      if (button) button.setAttribute('aria-expanded', 'false');
    });
  });
  __customSortGlobalEscapeBound = true;
}

// Initialize custom sort handlers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeCustomSortHandlers();
  });
} else {
  initializeCustomSortHandlers();
}

// Reinitialize after facet updates
const originalRenderAdditionalElements = FacetFiltersForm.renderAdditionalElements;
FacetFiltersForm.renderAdditionalElements = function(html) {
  originalRenderAdditionalElements.call(this, html);
  // Reinitialize custom sort handlers after content updates
  setTimeout(() => {
    initializeCustomSortHandlers();
  }, 100);
};